/**
 * Coerce API / form profile_data into a plain object for ProfileDataEditor.
 * Backend sometimes returns a JSON string; Object.entries(string) yields per-character keys.
 */

export function normalizeProfileData(raw: unknown): Record<string, unknown> {
  if (raw == null) return {};

  let value: unknown = raw;
  // Unwrap JSON strings (including double-encoded).
  for (let i = 0; i < 2; i += 1) {
    if (typeof value !== 'string') break;
    const trimmed = value.trim();
    if (!trimmed) return {};
    try {
      value = JSON.parse(trimmed);
    } catch {
      return { value: trimmed };
    }
  }

  if (Array.isArray(value)) {
    const out: Record<string, unknown> = {};
    value.forEach((entry, index) => {
      if (
        entry &&
        typeof entry === 'object' &&
        !Array.isArray(entry) &&
        'key' in entry &&
        typeof (entry as { key: unknown }).key === 'string'
      ) {
        const row = entry as { key: string; value: unknown };
        if (row.key.trim()) out[row.key] = row.value;
      } else {
        out[String(index)] = entry;
      }
    });
    return out;
  }

  if (typeof value === 'object') {
    return { ...(value as Record<string, unknown>) };
  }

  return { value };
}

/** Flatten nested `preferences` object into top-level keys for the key/value editor. */
export function flattenProfilePreferences(
  data: Record<string, unknown>
): Record<string, unknown> {
  const prefs = data.preferences;
  if (!prefs || typeof prefs !== 'object' || Array.isArray(prefs)) {
    return data;
  }
  const { preferences: _drop, ...rest } = data;
  const out: Record<string, unknown> = { ...rest };
  for (const [key, val] of Object.entries(prefs as Record<string, unknown>)) {
    out[`preferences.${key}`] = val;
  }
  return out;
}

/** Rebuild nested preferences from `preferences.*` keys before save. */
export function nestProfilePreferences(
  data: Record<string, unknown>
): Record<string, unknown> {
  const preferences: Record<string, unknown> = {};
  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(data)) {
    if (key.startsWith('preferences.')) {
      const sub = key.slice('preferences.'.length);
      if (sub) preferences[sub] = val;
    } else {
      out[key] = val;
    }
  }
  if (Object.keys(preferences).length > 0) {
    const existing =
      out.preferences && typeof out.preferences === 'object' && !Array.isArray(out.preferences)
        ? (out.preferences as Record<string, unknown>)
        : {};
    out.preferences = { ...existing, ...preferences };
  }
  return out;
}
